// The project screen as an editing suite (spec-v2.1.md B): the preview on top, the filmstrip of scenes, the audio
// timeline, and what's waiting on you. Phones stack it with the Director in a bar at the bottom; from 1024 px it's
// two panes, with the Director, review, scene detail, versions and brief in the side panel. Scene detail (prompts,
// model, sounds, cost) only opens when a scene is tapped.
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { LOGGED_LINE, renderWasLogged } from "../logged";
import { Empty, ErrorNote, fmtCredits, Icon, ROLE_LABEL, Sheet, Spinner, statusOf, stepLabel, useLoad } from "../components/bits";
import { DirectorBar } from "../components/Director";
import { useModels } from "../components/ModelPicker";
import { ReviewTray, useReview, useReviewReminder } from "../components/ReviewTray";
import { Shell } from "../components/Shell";
import { PACE, ProduceBar, ProduceSheet } from "../components/ShotBoard";
import { askBoard, Board, BOARD_FLAG, CommandBar, type BoardAsk } from "../components/Board";
import { Fold } from "../components/Fold";
import { castOf } from "../components/Cast";
import { isCommandKey, type Command } from "../board";
import { useFlag } from "../flags";
import { NextStep } from "../components/NextStep";
import { SunoSheet } from "../components/SunoPack";
import { Filmstrip, Preview, SceneDetail, Timeline } from "../components/Suite";
import { Versions } from "../components/Versions";
import { History } from "../components/History";
import { PublishKitFold } from "../components/PublishKit";
import { ModelRules } from "../components/ModelRules";
import { MoreMenu } from "../components/MoreMenu";
import { openUploadSheet, uploadSheetOpen } from "../components/UploadHost";
import { pastedFiles } from "../documents";
import { MoveSheet, placeMenuItems, PromoteSheet, SeriesFold, SeriesNotices, useVideoPlace, VideoCrumbs } from "../components/VideoPlace";
import { ThumbnailSheet } from "../components/ThumbnailSheet";
import { ProjectFilesSheet } from "./ProjectFiles";
import { FormatArt } from "../components/FormatArt";
import { FormatBadge, useFormats } from "../components/FormatMark";
import { FormatPanels, SourcesPanel } from "../components/VideoPanels";
import { WorldPanel } from "../components/World";
import { openWorld, WORLD_EVENT } from "../world";
import { ASPECT_WORD, fmtLen, formatOfProject, isAdProject, missingFields, videoBrief, type FormatInfo, type VideoBriefDraft } from "../formats";
import * as act from "../actions";
import { PaidButton, PriceSheet, usePrice } from "../components/PriceFirst"; // PRICE-FIRST
import { Notice } from "../components/Notice";
import { goWords, repriced, tapRule, type StepPrice } from "../price";
import { failureText } from "../produce";
import { director } from "../director";
import { nextReloadKey, nextTap, type NextStepData } from "../nextstep";
import { loadModelRules, sceneModelLine, tagWords } from "../modelrules";
import { characterPath } from "../characters";
import { Link, navigate, useLocation } from "../router";
import { FOLD_EVENT, setFold, type FoldEventDetail } from "../fold";
import { suiteFold } from "../suitefolds";
import { rememberProject } from "../session";
import { speakerChoices, withSpeaker } from "../speaker";
import { bump, get as storeGet, set, toast, useRefresh, useStore } from "../store";
import type { AdBrief, AdScene, AdScript, Aspect, Duration, Estimate, MyModels, ProduceMode, Project, Render, Version } from "../types";

/** Takes a param off the address without a new history entry or a scroll (the router hears it via popstate). */
function dropQuery(name: string) {
  const url = new URL(location.href);
  if (!url.searchParams.has(name)) return;
  url.searchParams.delete(name);
  history.replaceState(history.state, "", url.pathname + url.search + url.hash);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const ASPECT_NAME: Record<Aspect, string> = { "9:16": "Vertical 9:16", "16:9": "Wide 16:9", "1:1": "Square 1:1" };

function useWide() {
  const q = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => window.matchMedia?.(q).matches ?? false);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return;
    const f = () => setWide(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return wide;
}

type Tab = "review" | "scene" | "cast" | "versions" | "brief" | "board";
type Patch = Partial<Pick<Project, "script" | "title">> & { brief?: Partial<AdBrief> | VideoBriefDraft };

export function ProjectScreen({ id }: { id: string }) {
  const tick = useRefresh("project", "projects");
  const { data: p, setData, error, loading, reload } = useLoad(() => api.project(id), [id, tick]);
  const { data: renders } = useLoad(() => api.projectRenders(id).catch(() => [] as Render[]), [id, tick]);
  const { data: versions } = useLoad(() => api.versions(id).catch(() => [] as Version[]), [id, tick]);
  const { data: myModels } = useLoad<MyModels | null>(() => api.myModels().catch(() => null), []);
  // v4 (V4-P1): the one next step (core nextStep() via the API), in the slot under the title
  // fix-produce: re-read it when the project in hand changes (a script that just landed) and on every job or render
  // event ("next"), not only on a full refresh
  const nextTick = useRefresh("next");
  const nextKey = nextReloadKey(p);
  const { data: next } = useLoad<NextStepData | null>(() => api.nextStep(id).catch(() => null), [id, tick, nextTick, nextKey]);
  const [produceMode, setProduceMode] = useState<ProduceMode | null>(null);
  const [stepBusy, setStepBusy] = useState(false);
  // PRICE-FIRST: the script's price (the next step's "Write the script · 5"); over the user's ask-first amount it asks
  const scriptPrice = usePrice(id, "script");
  const [scriptAsk, setScriptAsk] = useState<{ price: StepPrice; notice: string | null } | null>(null);
  const models = useModels();
  const liveRender = useStore((s) => s.renders[id]);
  const wide = useWide();
  // /p/:id?scene=2 opens that scene's detail
  const { query } = useLocation();
  const linked = Number(query.get("scene")) || 0;
  const [sel, setSel] = useState(Math.max(0, linked - 1));
  const [detail, setDetail] = useState(linked > 0);
  const [tab, setTab] = useState<Tab | null>(linked > 0 ? "scene" : (query.get("tab") as Tab | null));
  const [editing, setEditing] = useState<AdScene | null>(null);
  const [thumbFor, setThumbFor] = useState<Version | null>(null);
  const [files, setFiles] = useState(false); // V4-DL: the "Download files" sheet
  // v4 (V4-M1): the Suno pack sheet (the next step "Upload your Suno song", or ?suno=1 from the new-video sheet)
  const [sunoOpen, setSunoOpen] = useState(query.get("suno") === "1");
  // v4 (V4-H2): where the video lives (channel > collection), its "from the series" values, Move to / Make a series
  const place = useVideoPlace({ id, channelId: p?.channelId ?? null });
  const [placeSheet, setPlaceSheet] = useState<"move" | "promote" | null>(null);
  const review = useReview(p ? [p] : null);
  useReviewReminder(review);
  // 2026-11-03 (James's report): paste a screenshot or a copied file anywhere on the page: the upload sheet opens with
  // it, for this video. Pasting into a text box stays a normal paste; an open sheet takes its own pastes.
  const title = p?.title;
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (uploadSheetOpen() || document.querySelector(".sheet, [role=dialog]")) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const picked = pastedFiles(e.clipboardData);
      if (!picked.length) return;
      e.preventDefault();
      openUploadSheet({ type: "upload_sheet", scope: `project:${id}`, scopeName: title ?? "this video", projectId: id }, picked);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [id, title]);
  const formats = useFormats();
  // flag hands_on_board: the Board (a side tab on desktop, a fold on phones) and the Ctrl/Cmd+K command bar
  const boardOn = useFlag(BOARD_FLAG);
  const [cmdOpen, setCmdOpen] = useState(false);
  useEffect(() => {
    if (!boardOn) return;
    const on = (e: KeyboardEvent) => {
      if (!isCommandKey(e)) return;
      e.preventDefault();
      setCmdOpen((o) => !o);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [boardOn]);

  // v4 (V4-P2, D3/D4 follow-up): a ?scene= that arrives while the video is already open (the Director's
  // show_references, or `fold` with a scene) picks that scene and opens its detail, like arriving with it. The param
  // is dropped once read, so asking for the same scene again later is a change too.
  const sceneParam = query.get("scene");
  useEffect(() => {
    const n = Number(sceneParam) || 0;
    if (n <= 0) return;
    setSel(n - 1);
    setTab("scene");
    setDetail(true);
    dropQuery("scene");
  }, [sceneParam]);

  // v4 (V4-P2): the Director opens a fold that isn't on screen yet (another side tab, or a scene's sheet on phones):
  // go to where it lives, then bring it into view
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<FoldEventDetail>).detail;
      const f = d?.open ? suiteFold(d.id) : null;
      if (!f) return;
      if (f.id && f.id !== d.id) setFold(f.id, true); // a bare name ("sounds"): open the exact fold it means
      if (d.id.trim().toLowerCase() === "places") openWorld("location");
      if (wide) setTab(f.tab);
      else if (f.tab === "scene") setDetail(true);
      window.setTimeout(() => {
        const el = (f.id ? document.querySelector(`[data-fold="${f.id}"]`) : null) ?? (wide ? null : document.getElementById(f.tab === "review" ? "tray-h" : f.tab === "cast" ? "world" : "suite-more"));
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 150);
    };
    window.addEventListener(FOLD_EVENT, on);
    return () => window.removeEventListener(FOLD_EVENT, on);
  }, [wide]);

  // 2026-11-02: a "Places and props" link elsewhere (a scene's References) opens the World: its side tab on desktop,
  // its section on phones
  useEffect(() => {
    const on = () => {
      if (wide) setTab("cast");
      else setDetail(false);
      if (!wide) window.setTimeout(() => document.getElementById("world")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    };
    window.addEventListener(WORLD_EVENT, on);
    return () => window.removeEventListener(WORLD_EVENT, on);
  }, [wide]);

  // a `play` for this project shows in the suite's preview, not the overlay
  useEffect(() => {
    set({ suiteProject: id });
    return () => set({ suiteProject: null });
  }, [id]);

  // seed the live render from history so a page opened mid-render shows progress straight away
  useEffect(() => {
    const latest = p?.latestRender ?? renders?.[0];
    if (latest && !storeGet().renders[id]) set((s) => ({ renders: { ...s.renders, [id]: latest } }));
  }, [renders, id, p?.latestRender]);
  useEffect(() => {
    if (p) rememberProject(p);
  }, [p]);

  const render = liveRender ?? renders?.[0];

  if (error && !p)
    return (
      <Shell>
        <div className="page">
          <BackLink />
          {error.toLowerCase().includes("not found") ? (
            <Empty title="This video isn't here" action={<Link to="/" className="btn btn-primary">Back to your videos</Link>}>
              It may have been deleted, or the link belongs to another account.
            </Empty>
          ) : (
            <ErrorNote text={`Couldn't open this video. ${error}`} retry={reload} />
          )}
        </div>
      </Shell>
    );
  if (!p)
    return (
      <Shell suite>
        <div className="page">{loading && <Spinner label="Opening the project" />}</div>
      </Shell>
    );

  const save = async (patch: Patch, said: string) => {
    const prev = p;
    setData({ ...p, ...patch, brief: { ...p.brief, ...(patch.brief as Partial<AdBrief>) } });
    try {
      const next = await api.patchProject(p.id, patch);
      setData(next);
      director.context(`The user ${said} by tapping on project "${p.title}" (${p.id}).`);
    } catch (e) {
      setData(prev);
      toast(`Couldn't save. ${(e as Error).message}`, "bad");
    }
  };

  const scenes = p.script?.scenes ?? [];
  const index = Math.min(sel, Math.max(0, scenes.length - 1));
  // v3: every format but the ad has its own brief, panels and (sometimes) a cast
  const ad = isAdProject(p);
  const format = ad ? null : (formats?.find((f) => f.id === formatOfProject(p)) ?? null);
  const saveBrief = (patch: VideoBriefDraft, said: string) => save({ brief: { ...videoBrief(p), ...patch } }, said);
  const vb = videoBrief(p);
  const lengthText = ad ? p.brief.durationS && `${p.brief.durationS} s` : vb.durationS && fmtLen(vb.durationS);
  const st = statusOf(p, render);
  const busy = !!render && (render.status === "queued" || render.status === "running");
  const latest = versions?.find((v) => v.isLatest) ?? null;
  const shownTab: Tab = tab ?? (review.length ? "review" : "scene");
  const select = (i: number) => {
    setSel(i);
    if (wide) setTab("scene");
    else setDetail(true);
  };
  const openItem = (sceneId?: string) => {
    const i = scenes.findIndex((s) => s.id === sceneId);
    if (i >= 0) select(i);
  };

  // a tap on the next step does what the Director's tool would (and tells the Director)
  const onNext = async (step: NextStepData) => {
    const t = nextTap(step, scenes.map((s) => s.id));
    const said = `tapped the next step "${step.label}"`;
    const show = (to: Tab) => {
      setTab(to);
      if (to === "cast") openWorld("characters");
      if (!wide) document.getElementById(to === "review" ? "tray-h" : to === "cast" ? "world" : "suite-more")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    switch (t.kind) {
      case "script": {
        // PRICE-FIRST: the price on the step is the yes under the ask-first amount; over it, the sheet asks
        const price: StepPrice | null = scriptPrice ?? (step.credits != null ? { step: "script", credits: step.credits, clips: 0, needsYes: false } : null);
        if (price && tapRule(price) === "ask") return setScriptAsk({ price, notice: null });
        setStepBusy(true);
        const r = await act.startStep(p.id, "script", {}, price?.credits ?? undefined);
        setStepBusy(false);
        if (r.kind === "ok") {
          if (r.project) setData(r.project);
          director.context(`The user ${said} on "${p.title}" (${p.id}): the script is written.`);
        } else if (r.kind === "reprice" && price) setScriptAsk({ price: repriced(price, r.price.credits), notice: r.text });
        else toast(`Couldn't write the script. ${r.text}`, "bad");
        return;
      }
      case "produce":
        setProduceMode({ scope: "all", through: t.through });
        return;
      case "approve":
        if (t.what === "music") await act.approveMusic(p.id);
        else if (t.sceneId) await act.approve(p.id, t.sceneId, t.what);
        return;
      case "retry":
        if (t.what === "music") await act.makeMusic(p.id);
        else if (!t.sceneId) return;
        else if (t.what === "frame") await act.makeFrame(p.id, t.sceneId);
        else if (t.what === "sounds") await act.makeSounds(p.id, t.sceneId);
        else if (t.model) await act.tryModel(p.id, t.sceneId, t.model, t.modelLabel ?? t.model);
        else await act.makeClip(p.id, t.sceneId);
        return;
      case "watch":
        await act.tapTool("play_video", { project_id: p.id }, `asked to watch "${p.title}"`);
        return;
      case "tab":
        show(t.tab);
        director.context(`The user ${said} on "${p.title}" (${p.id}).`);
        return;
      case "character":
        director.context(`The user ${said} on "${p.title}" (${p.id}): their character page is open.`);
        navigate(characterPath({ projectId: p.id }, t.character));
        return;
      case "suno":
        setSunoOpen(true);
        director.context(`The user ${said} on "${p.title}" (${p.id}): the Suno pack is open, with the upload at the bottom.`);
        return;
    }
  };

  // flag hands_on_board: show the Board (its tab, or its fold on phones), and the command bar's list
  const showBoard = (ask?: BoardAsk) => {
    if (wide) setTab("board");
    else {
      setDetail(false);
      setFold("video.board", true);
      window.setTimeout(() => document.querySelector('[data-fold="video.board"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
    if (ask) window.setTimeout(() => askBoard(ask), 60);
  };
  const showTab = (to: Exclude<Tab, "board">) => {
    if (wide) setTab(to);
    else if (to === "scene") setDetail(true);
    else document.getElementById(to === "review" ? "tray-h" : to === "cast" ? "world" : "suite-more")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (to === "cast") openWorld("characters");
  };
  const commands = (): Command[] => {
    const scripted = !!p.script;
    const pace = (through: (typeof PACE)[number]["through"], label: string, keywords: string): Command => {
      const m = PACE.find((x) => x.through === through)!;
      return { id: `produce-${through}`, label, hint: m.hint, keywords, disabled: !scripted, run: () => setProduceMode({ scope: "all", through }) };
    };
    const click = (sel: string, nope: string) => () => {
      const b = document.querySelector<HTMLButtonElement>(sel);
      if (b && !b.disabled) b.click();
      else toast(nope, "bad");
    };
    return [
      { id: "new-character", label: hosts ? "New host" : "New character", hint: "Someone who stays the same in every scene", keywords: "add person cast", run: () => showBoard("character") },
      { id: "new-scene", label: "New scene", hint: "Added at the end; free", keywords: "add", disabled: !scripted, run: () => showBoard("scene") },
      { id: "new-place", label: "New place", hint: "A room, a street, a landscape", keywords: "add location set world", run: () => showBoard("location") },
      { id: "new-prop", label: "New prop", hint: "A thing that comes back from scene to scene", keywords: "add object world", run: () => showBoard("prop") },
      { id: "add-files", label: "Add files", hint: "Pictures, songs, clips or a PDF for this video", keywords: "upload photos pictures songs music", run: () => openUploadSheet({ type: "upload_sheet", scope: `project:${p.id}`, scopeName: p.title, projectId: p.id }) },
      { id: "write-script", label: "Write the script", hint: "From the brief", keywords: "start", disabled: scripted, run: click('[data-action="script.write"]', "Fill in the brief first.") },
      pace("frames", "Make the frames", "all frames stills pictures produce"),
      pace("clips", "Animate approved", "clips video produce"),
      pace("render", "Finish it all", "everything produce"),
      { id: "render", label: "Render", hint: "A new version, price first", keywords: "final cut export video", disabled: !scripted, run: click(".render-btn", "Render isn't ready yet. Make the shots first, or add credits.") },
      { id: "board", label: "Open the Board", hint: "Characters, places and scenes as cards", keywords: "cards lanes", run: () => showBoard() },
      { id: "world", label: "Open the World", hint: "Characters, places and props", keywords: "cast", run: () => showTab("cast") },
      { id: "review", label: "Open the review", hint: "What waits for your OK", keywords: "approve", run: () => showTab("review") },
      { id: "versions", label: "Open the versions", hint: "Every render, publish and history", keywords: "history publish", run: () => showTab("versions") },
      { id: "brief", label: "Open the brief", hint: "Length, shape, look, voices", keywords: "settings", run: () => showTab("brief") },
      ...scenes.map((s, i) => ({ id: `scene-${i + 1}`, label: `Open scene ${i + 1}`, hint: s.onScreenText || s.voiceover, keywords: "go to", run: () => select(i) })),
      ...castOf(p).map((c) => ({ id: `char-${c.id}`, label: `Open ${c.name}`, hint: "Their sheet, looks and voice", keywords: "character", run: () => navigate(characterPath({ projectId: p.id }, c.id)) })),
      { id: "files", label: "Download files", hint: "Every picture, clip and sound", keywords: "export", run: () => setFiles(true) },
      { id: "copy", label: "Make a copy", hint: "Shares what's made; nothing is remade", keywords: "duplicate", run: () => void act.tapTool("duplicate_video", { project_id: p.id, as: "copy" }, `made a copy of "${p.title}"`) },
    ];
  };
  const hosts = format?.voices === "hosts";
  const board = boardOn && <Board p={p} format={format} onProject={setData} onOpenScene={select} />;

  const sceneDetail = <SceneDetail p={p} index={index} onEditWords={setEditing} models={models} myModels={myModels ?? null} />;
  const tray = <ReviewTray items={review} onOpen={(i) => openItem(i.sceneId)} />;
  // 2026-11-02: the video's World (characters, places, props): a side tab on desktop, a section on phones
  const world = (ad || format) && <WorldPanel p={p} format={format} onProject={setData} />;
  // restoring bumps "project", which reloads the versions too
  const versionList = (
    <>
      <Versions p={p} versions={versions} onRestored={setData} defaultOpen={wide} />
      <PublishKitFold p={p} latest={latest} />
      <History p={p} />
    </>
  );
  const brief = (
    <>
      {ad ? (
        <>
          <BriefSlate p={p} save={save} />
          {/* 2026-11-03: an ad's sources (a PDF added, a website read): the facts its script may state */}
          {!!videoBrief(p).sources && <SourcesPanel p={p} save={saveBrief} />}
        </>
      ) : format ? (
        <FormatPanels p={p} format={format} save={saveBrief} onProject={setData} without={["cast"]} />
      ) : (
        <Spinner label="Loading the brief" />
      )}
      {p.script && <Rewrite p={p} onChange={setData} />}
      {p.script && <ModelRules p={p} />}
    </>
  );

  return (
    <Shell suite>
      <div className={`suite${p.script ? "" : " suite-unscripted"}`}>
        <header className="suite-head">
          <VideoCrumbs p={p} place={place} />
          <div className="suite-title">
            <h1 className="page-title">{p.title}</h1>
            <p className="suite-sub">
              <FormatBadge format={formatOfProject(p)} />
              <span className={`status status-${st.tone} status-inline`}>{st.label}</span>
              <span className="muted">{[lengthText, p.brief.aspect && ASPECT_NAME[p.brief.aspect as Aspect]].filter(Boolean).join(", ")}</span>
            </p>
          </div>
          {!!review.length && (
            <button
              type="button"
              className="review-chip"
              onClick={() => (wide ? setTab("review") : document.getElementById("tray-h")?.scrollIntoView({ behavior: "smooth", block: "center" }))}
              aria-label={`${review.length} waiting for your review`}
            >
              <span className="count is-on">{review.length}</span> Needs your review
            </button>
          )}
          {/* 2026-11-03 (James's report): add files here, not only through the Director or the Library */}
          <button type="button" className="btn btn-quiet btn-small suite-add" data-action="project.upload" onClick={() => openUploadSheet({ type: "upload_sheet", scope: `project:${p.id}`, scopeName: p.title, projectId: p.id })}>
            <Icon name="plus" size={16} /> Add files
          </button>
          <MoreMenu
            label="More for this video"
            items={[
              {
                id: "thumbnail",
                label: "Change thumbnail",
                hint: latest ? `The picture on v${latest.version}'s card` : "Once the first version is made",
                icon: "image",
                disabled: !latest,
                onSelect: () => latest && setThumbFor(latest),
                dataAction: "thumbnail.pick",
              },
              // 2026-11-03 (Keaton's report): the Suno pack from anywhere on the page, not only the music strip
              ...(p.suno ? [{ id: "suno", label: "Suno pack", hint: `Version ${p.suno.version}${p.suno.history.length ? `, and ${p.suno.history.length} earlier` : ""}`, icon: "music" as const, onSelect: () => setSunoOpen(true), dataAction: "suno.open" }] : []),
              { id: "files", label: "Download files", hint: "Every picture, clip and sound, in its own format", icon: "download", onSelect: () => setFiles(true), dataAction: "project.files" },
              // v4 (D6 parity with duplicate_video): an exact copy that shares every made picture, clip and song
              { id: "copy", label: "Make a copy", hint: "Shares what's made; nothing is remade", icon: "copy", onSelect: () => void act.tapTool("duplicate_video", { project_id: p.id, as: "copy" }, `made a copy of "${p.title}"`), dataAction: "video.duplicate" },
              ...placeMenuItems(p, place, setPlaceSheet),
            ]}
          />
        </header>

        {p.script && <NextStep step={next} onAct={(s) => void onNext(s)} pending={stepBusy} />}
        <SeriesNotices p={p} place={place} />
        <SeriesFold place={place} />

        <div className="suite-grid">
          <div className="suite-main">
            {p.script ? (
              <>
                <Preview p={p} index={index} render={render} version={latest} />
                <Filmstrip p={p} selected={index} onSelect={select} />
                <Timeline p={p} selected={index} onSelect={(i) => setSel(i)} onProject={setData} />
                <div className="transport">
                  <ProduceBar p={p} />
                  <RenderControl p={p} busy={busy} render={render} />
                </div>
              </>
            ) : (
              <ScriptStart p={p} format={format} onChange={setData} />
            )}
            {!wide && (
              <div className="suite-stack" id="suite-more">
                {board && (
                  <Fold id="video.board" title="Board" summary="Characters, places and scenes as cards" className="board-fold">
                    {board}
                  </Fold>
                )}
                {p.script && tray}
                {world}
                {p.script && versionList}
                {brief}
              </div>
            )}
          </div>

          {wide && (
            <aside className="suite-side" aria-label="Director and details">
              <DirectorBar variant="panel" />
              <div className={`side-tabs${boardOn ? " has-board" : ""}`} role="tablist" aria-label="Details">
                {(
                  [
                    ["review", `Review`],
                    ["scene", scenes.length ? `Scene ${index + 1}` : "Scene"],
                    ...(ad || format ? [["cast", "World"]] : []),
                    ...(boardOn ? [["board", "Board"]] : []),
                    ["versions", "Versions"],
                    ["brief", "Brief"],
                  ] as [Tab, string][]
                ).map(([t, label]) => (
                  <button key={t} type="button" role="tab" aria-selected={shownTab === t} className={shownTab === t ? "is-on" : ""} onClick={() => setTab(t)} disabled={t === "scene" && !p.script}>
                    {label}
                    {t === "review" && <span className={`count${review.length ? " is-on" : ""}`}>{review.length}</span>}
                  </button>
                ))}
              </div>
              <div className="side-body" role="tabpanel">
                {shownTab === "review" && tray}
                {shownTab === "scene" && (p.script ? sceneDetail : <p className="muted">Write the script to see its scenes.</p>)}
                {shownTab === "cast" && world}
                {shownTab === "versions" && versionList}
                {shownTab === "brief" && brief}
                {shownTab === "board" && board}
              </div>
            </aside>
          )}
        </div>
      </div>

      {detail && !wide && scenes[index] && (
        <Sheet title={`Scene ${index + 1}`} onClose={() => setDetail(false)} className="detail-sheet">
          {sceneDetail}
        </Sheet>
      )}
      {produceMode && <ProduceSheet p={p} mode={produceMode} onClose={() => setProduceMode(null)} />}
      {boardOn && cmdOpen && <CommandBar commands={commands()} onClose={() => setCmdOpen(false)} />}
      {scriptAsk && (
        <PriceSheet
          ask={{ title: "Write the script?", lede: "Your Director writes the scenes from the brief. You can change any of them after.", go: "Write it" }}
          price={scriptAsk.price}
          notice={scriptAsk.notice}
          run={(credits) => act.startStep(p.id, "script", {}, credits)}
          onDone={(r) => r.project && setData(r.project)}
          onClose={() => setScriptAsk(null)}
        />
      )}
      {thumbFor && <ThumbnailSheet p={p} v={thumbFor} onClose={() => setThumbFor(null)} />}
      {files && <ProjectFilesSheet p={p} onClose={() => setFiles(false)} />}
      {sunoOpen && <SunoSheet p={p} onClose={() => setSunoOpen(false)} />}
      {placeSheet === "move" && <MoveSheet p={p} place={place} onClose={() => setPlaceSheet(null)} />}
      {placeSheet === "promote" && <PromoteSheet p={p} onClose={() => setPlaceSheet(null)} />}
      {editing && p.script && (
        <SceneEdit
          p={p}
          scene={editing}
          onClose={() => setEditing(null)}
          onSave={async (next, tags) => {
            const s: AdScript = { ...p.script!, scenes: p.script!.scenes.map((x) => (x.id === next.id ? next : x)) };
            const n = p.script!.scenes.findIndex((x) => x.id === next.id) + 1;
            setEditing(null);
            await save({ script: s }, `edited scene "${next.onScreenText}"`);
            // v4 (D6): the scene's tags go through edit_scene (they pick its video model)
            if (tags !== null && n > 0) await act.tapTool("edit_scene", { project_id: p.id, scene: n, tags }, `tagged scene ${n} "${tags || "from its words"}"`);
          }}
          onStructure={(i) => {
            setEditing(null);
            if (i !== null) setSel(i);
          }}
        />
      )}
    </Shell>
  );
}

function BackLink() {
  return (
    <Link to="/" className="back">
      <Icon name="back" size={18} /> Your videos
    </Link>
  );
}

// ------------------------------------------------------------------ script

function ScriptStart({ p, format, onChange }: { p: Project; format: FormatInfo | null; onChange: (p: Project) => void }) {
  if (!isAdProject(p)) return <VideoScriptStart p={p} format={format} onChange={onChange} />;
  const [writing, setWriting] = useState(false);
  return (
    <section className="script-start" aria-label="Script">
      <div className={`preview-frame frame-${((p.brief.aspect ?? "9:16") as Aspect).replace(":", "x")} script-start-frame`}>
        <span className="preview-empty">{writing ? "Writing five scenes" : "Your scenes appear here"}</span>
        {writing && <span className="frame-scan" aria-hidden />}
      </div>
      <div className="script-start-text">
        <h2>Start with the script</h2>
        <p>{p.brief.product ? "The brief is enough to start. Your Director writes five short scenes: a hook, the problem, the benefit, proof, and the call to action." : "Fill in at least the product, then write the script. Or just tell your Director about it."}</p>
        {writing ? (
          <Spinner label="Writing from your brief" />
        ) : (
          <WriteScriptButton p={p} disabled={!p.brief.product} onChange={onChange} setWriting={setWriting} />
        )}
      </div>
    </section>
  );
}

/** Script start for every format but the ad: the format's plate while it writes, and what's still missing. */
function VideoScriptStart({ p, format, onChange }: { p: Project; format: FormatInfo | null; onChange: (p: Project) => void }) {
  const [writing, setWriting] = useState(false);
  const b = videoBrief(p);
  const missing = format ? missingFields(format, b) : [];
  const fmt = formatOfProject(p);
  const aspect = (b.aspect ?? format?.aspects[0] ?? "16:9") as Aspect;
  return (
    <section className="script-start" aria-label="Script">
      <div className={`preview-frame frame-${aspect.replace(":", "x")} script-start-frame script-start-plate`}>
        <FormatArt format={fmt} />
        {writing && <span className="frame-scan" aria-hidden />}
        <span className="preview-empty">{writing ? "Writing the scenes" : ""}</span>
      </div>
      <div className="script-start-text">
        <h2>Start with the script</h2>
        <p>
          {missing.length
            ? `Fill in ${missing.map((f) => f.key === "idea" ? "the idea" : f.key).join(" and ")} first, or tell your Director about it.`
            : `Your Director writes it from the brief: ${[vb(b.durationS), b.aspect && ASPECT_WORD[b.aspect].toLowerCase(), format?.looks.find((l) => l.id === b.look)?.name.toLowerCase()].filter(Boolean).join(", ")}. You can change any scene after.`}
        </p>
        {writing ? (
          <Spinner label="Writing from your brief" />
        ) : (
          <WriteScriptButton p={p} disabled={!!missing.length || !format} onChange={onChange} setWriting={setWriting} />
        )}
      </div>
    </section>
  );
}
const vb = (d?: number) => (d ? fmtLen(d) : null);

/** PRICE-FIRST: "Write the script · 5": under the ask-first amount the tap is the yes; over it, a sheet asks first. */
function WriteScriptButton({ p, disabled, onChange, setWriting }: { p: Project; disabled: boolean; onChange: (p: Project) => void; setWriting: (b: boolean) => void }) {
  const price = usePrice(p.id, "script");
  return (
    <PaidButton
      price={price}
      label="Write the script"
      className="btn btn-primary"
      dataAction="script.write"
      disabled={disabled}
      ask={{ title: "Write the script?", lede: "Your Director writes the scenes from the brief. You can change any of them after.", go: "Write it" }}
      run={async (credits) => {
        setWriting(true);
        try {
          const r = await act.startStep(p.id, "script", {}, credits);
          if (r.kind === "ok" && r.project) onChange(r.project);
          return r;
        } finally {
          setWriting(false);
        }
      }}
    />
  );
}

function Rewrite({ p, onChange }: { p: Project; onChange: (p: Project) => void }) {
  const [open, setOpen] = useState(false);
  const [writing, setWriting] = useState(false);
  const price = usePrice(p.id, "script");
  // PRICE-FIRST: the sheet's button carries the price ("Rewrite for 5 credits") and sends it; a dearer one asks again
  const write = async (instructions: string, credits: number | undefined) => {
    setWriting(true);
    try {
      const r = await act.startStep(p.id, "script", { note: instructions }, credits);
      if (r.kind === "ok" && r.project) onChange(r.project);
      return r;
    } finally {
      setWriting(false);
    }
  };
  return (
    <div className="rewrite">
      <p className="muted">{[p.script?.title, `Music: ${p.script?.musicMood}`].filter(Boolean).join(". ")}</p>
      <button type="button" className="btn btn-quiet btn-small" onClick={() => setOpen(true)} disabled={writing}>
        <Icon name="edit" size={14} /> {writing ? "Rewriting" : "Rewrite the script"}
      </button>
      {open && (
        <RewriteSheet price={price} onClose={() => setOpen(false)} onRewrite={write} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ brief

type BriefKey = keyof AdBrief;
const FIELDS: { key: BriefKey; label: string; placeholder: string; long?: boolean }[] = [
  { key: "product", label: "Product", placeholder: "What you're selling" },
  { key: "tone", label: "Tone", placeholder: "Warm, funny, urgent…" },
  { key: "audience", label: "Audience", placeholder: "Who it's for", long: true },
  { key: "offer", label: "Offer", placeholder: "Optional: a deal or a reason to act now", long: true },
  { key: "cta", label: "Call to action", placeholder: "What should people do?", long: true },
];

function BriefSlate({ p, save }: { p: Project; save: (patch: Patch, said: string) => Promise<void> }) {
  const [editing, setEditing] = useState<BriefKey | null>(null);
  const b = p.brief;
  const missing = FIELDS.filter((f) => f.key !== "offer" && !b[f.key]).length;
  return (
    <section className="slate" aria-labelledby="brief-h">
      <header className="slate-head">
        <h2 id="brief-h">Brief</h2>
        <span className="slate-note">{missing ? `${missing} to fill in` : "Complete"}</span>
      </header>
      <dl className="slate-grid">
        {FIELDS.map((f) => (
          <button type="button" key={f.key} className={`slate-cell${f.long ? " slate-cell-wide" : ""}${b[f.key] ? "" : " is-empty"}`} onClick={() => setEditing(f.key)}>
            <dt>{f.label}</dt>
            <dd>{(b[f.key] as string) || f.placeholder}</dd>
          </button>
        ))}
        <div className="slate-cell slate-cell-wide slate-seg">
          <dt>Length</dt>
          <dd>
            <Segmented<Duration> value={b.durationS} options={[15, 30, 60]} label={(d) => `${d} s`} onChange={(d) => void save({ brief: { ...b, durationS: d } }, `set the length to ${d} seconds`)} />
          </dd>
        </div>
        <div className="slate-cell slate-cell-wide slate-seg">
          <dt>Shape</dt>
          <dd>
            <Segmented<Aspect> value={b.aspect} options={["9:16", "1:1", "16:9"]} label={(a) => ASPECT_NAME[a].split(" ")[0]} onChange={(a) => void save({ brief: { ...b, aspect: a } }, `set the shape to ${a}`)} />
          </dd>
        </div>
        <Link to="/brand" className="slate-cell slate-cell-wide">
          <dt>Brand</dt>
          <dd>{b.brand || "No brand yet. Add one"}</dd>
        </Link>
      </dl>
      {editing && <BriefEdit field={FIELDS.find((f) => f.key === editing)!} value={(b[editing] as string) ?? ""} onClose={() => setEditing(null)} onSave={(v) => save({ brief: { ...b, [editing]: v } }, `changed the brief's ${editing} to "${v}"`)} />}
    </section>
  );
}

function Segmented<T extends string | number>({ value, options, label, onChange }: { value?: T; options: T[]; label: (v: T) => string; onChange: (v: T) => void }) {
  return (
    <span className="seg" role="radiogroup">
      {options.map((o) => (
        <button data-action="brief.choose" type="button" role="radio" aria-checked={o === value} key={String(o)} className={o === value ? "is-on" : ""} onClick={() => o !== value && onChange(o)}>
          {label(o)}
        </button>
      ))}
    </span>
  );
}

function BriefEdit({ field, value, onClose, onSave }: { field: (typeof FIELDS)[number]; value: string; onClose: () => void; onSave: (v: string) => Promise<void> }) {
  const [v, setV] = useState(value);
  return (
    <Sheet title={field.label} onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void onSave(v.trim());
          onClose();
        }}
      >
        <label className="field">
          <span className="field-label">{field.placeholder}</span>
          {field.long ? <textarea value={v} onChange={(e) => setV(e.target.value)} rows={3} data-autofocus /> : <input value={v} onChange={(e) => setV(e.target.value)} data-autofocus />}
        </label>
        <button type="submit" className="btn btn-primary btn-block">
          Save
        </button>
      </form>
    </Sheet>
  );
}

function RewriteSheet({ price, onClose, onRewrite }: { price: StepPrice | null | undefined; onClose: () => void; onRewrite: (t: string, credits: number | undefined) => Promise<act.PaidOutcome> }) {
  const [t, setT] = useState("");
  const [now, setNow] = useState<{ price: StepPrice; text: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [going, setGoing] = useState(false);
  const cur = now?.price ?? price;
  return (
    <Sheet title="Rewrite the script" onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!t.trim()) return;
          setGoing(true);
          setFailure(null);
          const r = await onRewrite(t.trim(), cur?.credits ?? undefined);
          setGoing(false);
          if (r.kind === "ok") return onClose();
          if (r.kind === "reprice" && cur) setNow({ price: repriced(cur, r.price.credits), text: r.text });
          else if (r.kind !== "reprice") setFailure(r.text);
        }}
      >
        <label className="field">
          <span className="field-label">What should change?</span>
          <textarea value={t} onChange={(e) => setT(e.target.value)} rows={3} placeholder="Funnier hook. Mention free delivery." data-autofocus />
        </label>
        {now && <Notice tone="warning" text={`${now.text} Go ahead?`} code="CONFIRM_REQUIRED" />}
        {failure && <Notice tone="error" text={`Couldn't rewrite it. ${failure}`} />}
        <button data-action="script.rewrite" type="submit" className="btn btn-primary btn-block" disabled={!t.trim() || going || cur === undefined}>
          {going ? "Rewriting" : goWords("Rewrite", cur?.credits)}
        </button>
      </form>
    </Sheet>
  );
}

/**
 * A scene's words, picture and (v4 D6) tags, plus the structure edits the Director makes by voice: earlier / later,
 * split, join with the next, add one after, delete. Each is the same tool (reorder_scenes, split_scene, merge_scenes,
 * add_scene, delete_scene), free, and undoable from History.
 */
function SceneEdit({ p, scene, onClose, onSave, onStructure }: { p: Project; scene: AdScene; onClose: () => void; onSave: (s: AdScene, tags: string | null) => void; onStructure: (select: number | null) => void }) {
  const [vo, setVo] = useState(scene.voiceover);
  const [ost, setOst] = useState(scene.onScreenText);
  const [img, setImg] = useState(scene.imagePrompt);
  const speakers = speakerChoices(p); // 2026-11-04: an ad with characters: who says the words
  const [speaker, setSpeaker] = useState<string | null>(scene.speaker ?? null);
  const was = (scene.tags ?? []).join(", ");
  const [tags, setTags] = useState(was);
  const [adding, setAdding] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const scenes = p.script?.scenes ?? [];
  const i = scenes.findIndex((x) => x.id === scene.id);
  const n = i + 1;
  const count = scenes.length;
  const { data: rules } = useLoad(() => loadModelRules(p.id, scenes.map((x) => x.id)).catch(() => null), [p.id]);
  const inferred = rules?.scenes.find((x) => x.sceneId === scene.id);
  const structure = async (tool: string, args: Record<string, unknown>, said: string, select: number | null) => {
    setBusy(true);
    const r = await act.tapTool(tool, { project_id: p.id, ...args }, said);
    setBusy(false);
    if (r) {
      bump("project");
      onStructure(select);
    }
  };
  const order = (from: number, to: number) => {
    const o = scenes.map((_, k) => k + 1);
    [o[from], o[to]] = [o[to]!, o[from]!];
    return o.join(",");
  };
  return (
    <Sheet title={`Scene ${n}${ROLE_LABEL[scene.role] ? `: ${ROLE_LABEL[scene.role]}` : ""}`} onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={(e) => {
          e.preventDefault();
          const t = tagWords(tags);
          const next = { ...scene, voiceover: vo.trim(), onScreenText: ost.trim(), imagePrompt: img.trim() };
          onSave(speakers.length ? withSpeaker(next, speaker) : next, t === tagWords(was) ? null : t);
        }}
      >
        <label className="field">
          <span className="field-label">Voiceover</span>
          <textarea value={vo} onChange={(e) => setVo(e.target.value)} rows={3} />
        </label>
        {speakers.length > 0 && (
          <label className="field">
            <span className="field-label">Said by</span>
            <select data-action="scene.speaker" value={speaker ?? ""} onChange={(e) => setSpeaker(e.target.value || null)}>
              {speakers.map((c) => (
                <option key={c.id ?? ""} value={c.id ?? ""}>
                  {c.label}
                </option>
              ))}
            </select>
            <span className="field-hint">A character says it on screen in their voice, lip-synced. Their line and the lip sync are in the clip's price; redo the frame and clip after a change.</span>
          </label>
        )}
        <label className="field">
          <span className="field-label">On-screen text</span>
          <input value={ost} onChange={(e) => setOst(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Picture</span>
          <textarea value={img} onChange={(e) => setImg(e.target.value)} rows={2} />
        </label>
        <label className="field">
          <span className="field-label">Tags</span>
          <input value={tags} onChange={(e) => setTags(e.target.value)} maxLength={200} placeholder={inferred?.tags.length ? `From its words: ${inferred.tags.join(", ")}` : "action, close-up, product…"} />
          <span className="field-hint">{inferred ? `${sceneModelLine(inferred)}. ` : ""}Tags pick the scene's video model when you have model rules.</span>
        </label>
        <button type="submit" className="btn btn-primary btn-block" data-action="scene.edit">
          Save scene
        </button>
      </form>

      <div className="sheet-body scene-struct" aria-label="Move, split or remove this scene">
        <p className="field-label">This scene</p>
        <div className="scene-struct-row">
          <button type="button" className="btn btn-quiet btn-small" data-action="scene.move" disabled={busy || i <= 0} onClick={() => void structure("reorder_scenes", { order: order(i, i - 1) }, `moved scene ${n} earlier`, i - 1)}>
            Earlier
          </button>
          <button type="button" className="btn btn-quiet btn-small" data-action="scene.move" disabled={busy || i >= count - 1} onClick={() => void structure("reorder_scenes", { order: order(i, i + 1) }, `moved scene ${n} later`, i + 1)}>
            Later
          </button>
          <button type="button" className="btn btn-quiet btn-small" data-action="scene.split" disabled={busy} onClick={() => void structure("split_scene", { scene: n }, `split scene ${n} in two`, i)}>
            Split
          </button>
          <button type="button" className="btn btn-quiet btn-small" data-action="scene.merge" disabled={busy || i >= count - 1} onClick={() => void structure("merge_scenes", { scene: n }, `joined scene ${n} with the next`, i)}>
            Join with next
          </button>
          <button type="button" className="btn btn-quiet btn-small" data-action="scene.add" disabled={busy} onClick={() => setAdding(adding === null ? "" : null)} aria-expanded={adding !== null}>
            Add one after
          </button>
          <button type="button" className="btn btn-quiet btn-small scene-struct-del" data-action="scene.delete" disabled={busy || count <= 1} onClick={() => setDeleting(true)}>
            Delete
          </button>
        </div>
        {adding !== null && (
          <form
            className="scene-struct-add"
            onSubmit={(e) => {
              e.preventDefault();
              if (adding.trim()) void structure("add_scene", { after: n, description: adding.trim() }, `added a scene after scene ${n}`, i + 1);
            }}
          >
            <label className="field">
              <span className="field-label">What does the new scene show?</span>
              <input value={adding} onChange={(e) => setAdding(e.target.value)} maxLength={300} placeholder="Close-up of the mug, steam rising" autoFocus />
            </label>
            <button type="submit" className="btn btn-primary btn-small" data-action="scene.add" disabled={busy || !adding.trim()}>
              Add it
            </button>
          </form>
        )}
        {deleting && (
          <div className="scene-struct-confirm" role="alert">
            <p>Remove scene {n}? Its pictures stay in the library, and you can undo it from History.</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-quiet btn-small" onClick={() => setDeleting(false)}>
                Keep it
              </button>
              <button type="button" className="btn btn-danger btn-small" data-action="scene.delete" disabled={busy} onClick={() => void structure("delete_scene", { scene: n }, `removed scene ${n}`, Math.max(0, i - 1))}>
                Remove scene {n}
              </button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ render

/** The transport's render button: the price on it, a confirm with the breakdown, live progress while it runs. */
function RenderControl({ p, busy, render }: { p: Project; busy: boolean; render?: Render }) {
  const balance = useStore((s) => s.balance);
  const scriptKey = JSON.stringify([p.script?.scenes.map((s) => s.voiceover), p.brief.durationS, p.brief.aspect]);
  const { data: est, error, reload } = useLoad<Estimate | null>(() => (p.script ? api.estimate(p.id) : Promise.resolve(null)), [p.id, scriptKey]);
  const [confirm, setConfirm] = useState(false);
  const [starting, setStarting] = useState(false);
  const short = !!est && balance != null && balance < est.credits;

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.render(p.id, est?.credits); // cost audit: never charged above the price on the confirm
      set((s) => ({ renders: { ...s.renders, [p.id]: r } }));
      director.context(`The user confirmed and started the render of "${p.title}" by tapping (${est?.credits ?? "?"} credits).`);
      setConfirm(false);
      bump("credits", "projects");
    } catch (e) {
      if (e instanceof ApiError && e.code === "CONFIRM_REQUIRED") {
        // the price went up since it was shown: show the new one and ask again
        reload();
        toast("The price changed. Check the new price and tap Render again.", "bad");
      } else toast(e instanceof ApiError && e.status === 402 ? "Not enough credits for this render." : `Couldn't start the render. ${failureText(e)}`, "bad");
    } finally {
      setStarting(false);
    }
  };

  if (busy && render)
    return (
      <div className="render-live" aria-label="Render progress">
        <div className="bar" role="progressbar" aria-valuenow={Math.round(render.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${Math.round(render.progress * 100)}%` }} />
        </div>
        <p>
          {stepLabel(render.status === "queued" ? "queued" : render.step)}{" "}
          <span className="step-job-end">
            <b>{Math.round(render.progress * 100)}%</b>
            {/* v4 (D6 parity with cancel_job): stop the render; its credits come back */}
            <button type="button" className="step-job-stop" data-action="job.cancel" onClick={() => void act.tapTool("cancel_job", { project_id: p.id, which: "render" }, `stopped the render of "${p.title}"`)} aria-label="Stop the render. Its credits come back">
              Stop
            </button>
          </span>
        </p>
      </div>
    );

  return (
    <>
      {render?.status === "failed" && <p className="form-error render-failed">The last render stopped: {render.error ?? "unknown error"}. Its credits came back.</p>}
      {render?.status === "failed" && renderWasLogged(render.error) && <p className="logged-note">{LOGGED_LINE}</p>}
      <button type="button" className="btn btn-primary render-btn" disabled={!est || !!short} onClick={() => setConfirm(true)} title={error ?? undefined}>
        <Icon name="film" size={16} /> {p.status === "done" || render?.status === "done" ? "Render a new version" : isAdProject(p) ? "Render the ad" : "Render the video"}
        {est && <span className="render-cost">{fmtCredits(est.credits)}</span>}
      </button>
      {short && (
        <Link to="/credits" className="muted render-short">
          You need {fmtCredits((est?.credits ?? 0) - (balance ?? 0))} more credits
        </Link>
      )}
      {confirm && est && (
        <Sheet title={isAdProject(p) ? "Render this ad?" : "Render this video?"} onClose={() => setConfirm(false)}>
          <div className="sheet-body confirm">
            <p className="confirm-num">
              {fmtCredits(est.credits)} <span>credits</span>
            </p>
            <ul className="est-lines">
              {est.breakdown.map((l) => (
                <li key={l.label}>
                  <span>
                    {l.label}
                    {l.detail && <small>{l.detail}</small>}
                  </span>
                  <span>{fmtCredits(l.credits)}</span>
                </li>
              ))}
            </ul>
            <p>
              Every approved clip (or the frame where there's no clip), the {isAdProject(p) ? "voiceover" : "voices"}, the music and the sounds become a {isAdProject(p) ? `${p.brief.durationS ?? 30}-second` : fmtLen(Number(p.brief.durationS ?? 60))} {ASPECT_NAME[(p.brief.aspect ?? "9:16") as Aspect].toLowerCase()} video. It becomes a new version; the old ones stay. If anything fails, the credits come back.
            </p>
            <p className="muted">Balance after: {fmtCredits((balance ?? 0) - est.credits)} credits.</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-quiet" onClick={() => setConfirm(false)}>
                Not yet
              </button>
              <button data-action="render.go" type="button" className="btn btn-primary" onClick={() => void start()} disabled={starting} data-autofocus>
                {starting ? "Starting" : `Render for ${fmtCredits(est.credits)}`}
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </>
  );
}
